import { useQuery, useQueryClient } from '@tanstack/react-query';

import {
  fetchAskFfhEligibility,
  type AskFfhEligibility
} from '@api-service/ask-ffh';
import { useAccessToken } from '@hooks/useAccessToken';
import { useAuth0SessionUser } from '@hooks/useAuth0SessionUser';

export const ASK_FFH_ELIGIBILITY_QUERY_KEY = ['ask-ffh-eligibility'] as const;

// The beta grows by PostHog flag, so a member let in mid-visit sees the menu
// item within a few minutes rather than on their next login.
const ELIGIBILITY_STALE_TIME_IN_MS = 5 * 60 * 1000;

// A failed read is tried again before the page gives up on it.
const ELIGIBILITY_RETRIES = 2;

/**
 * Whether the signed-in member is in the Ask FFH beta. `isEligible` is a
 * definite yes and `isNotEligible` a definite no; while the answer loads, or
 * when it cannot be read, both are false, so the menu item only appears on a
 * yes and the page only turns a member away on a no.
 */
export const useAskFfhEligibility = (): {
  isEligible: boolean;
  isNotEligible: boolean;
  hasEligibilityFailed: boolean;
  isEligibilityLoading: boolean;
} => {
  const { user, isLoading: isUserLoading } = useAuth0SessionUser();
  const { accessToken, accessTokenIsFetching } = useAccessToken();
  const queryClient = useQueryClient();
  const queryKey = [...ASK_FFH_ELIGIBILITY_QUERY_KEY, user?.sub];

  // A deployment without Ask FFH stays without it for the whole visit, so
  // that answer is kept rather than asked for again every few minutes.
  const isNotOffered =
    queryClient.getQueryData<AskFfhEligibility>(queryKey)?.isOffered === false;

  const { data, isLoading, isError } = useQuery({
    queryKey,
    queryFn: () => fetchAskFfhEligibility(accessToken as string),
    enabled: Boolean(user) && Boolean(accessToken),
    staleTime: isNotOffered ? Infinity : ELIGIBILITY_STALE_TIME_IN_MS,
    retry: ELIGIBILITY_RETRIES
  });

  const isWaitingForToken =
    Boolean(user) && !accessToken && accessTokenIsFetching;
  const hasNoToken = Boolean(user) && !accessToken && !accessTokenIsFetching;
  return {
    isEligible: data?.isEligible === true,
    isNotEligible: data?.isEligible === false,
    hasEligibilityFailed: isError || hasNoToken,
    isEligibilityLoading:
      isUserLoading ||
      isWaitingForToken ||
      (isLoading && Boolean(user) && Boolean(accessToken))
  };
};
